<template>
  <div class="content-wrapper step-2">
    <div class="step-wrapper">
      <div class="step">Etape 2/3</div>
      <div class="header-content">
        <h1>Pondérez votre campagne</h1>
      </div>
      <div class="ponderation-wrapper">
        <YuzuAiBanner />
        <h3>Configuration du métier et de la pondération</h3>
        <p class="subtitle jobchoice" v-if="!useJob">
          Votre campagne ne cible aucun métier en particulier.
          <span class="yuzuai">Yuzu AI</span> ne peut donc pas formuler de
          recommandation.
        </p>
        <p class="subtitle jobchoice" v-else-if="foundJobs">
          À partir de votre saisie, voici les métiers les plus proches
          identifiés par <span class="yuzuai">Yuzu AI</span> :
          <v-select
            placeholder="Sélectionnez un métier similaire"
            :options="jobChoices"
            label="job_title"
            v-model="selectedJob"
            @update:modelValue="jobSelected"
          />
        </p>
        <p class="subtitle jobchoice" v-else>
          Le métier que vous avez renseigné n’a pas pu être identifié
          précisément par <span class="yuzuai">Yuzu AI</span>.
        </p>
        <p class="subtitle adjust">
          Vous pouvez ajuster les recommandations de
          <span class="yuzuai">Yuzu AI</span> en faisant glisser les compétences
          entre les colonnes. Ces choix serront modifiables par la suite.
        </p>
        <div class="duration">
          <ButtonGroup
            :buttons="versionOptions"
            @click="selectVersion"
            :defaultIndex="1"
          />
        </div>
        <div class="ponderation-skills-wrapper">
          <draggable
            v-if="state.ponderation_type === 'coef'"
            :list="state.ponderation_obj"
            :group="{ name: 'g1' }"
            item-key="id"
            class="drag-and-drop-wrapper"
          >
            <template #item="{ element }">
              <div class="ponderation-skill-container">
                <span class="title">{{ element.label }}</span>
                <draggable
                  :list="element.skills"
                  item-key="id"
                  class="skills-wrapper"
                  :group="{ name: 'g2' }"
                >
                  <template #header v-if="element.skills.length === 0">
                    <div class="no-items">
                      Aucune compétence n’a été attribuée à cette colonne.
                    </div>
                  </template>
                  <template #item="{ element }">
                    <div
                      class="skill tooltip"
                      :class="{ disabled: !isSkillCompatible(element.id) }"
                    >
                      <span
                        class="tooltiptext"
                        v-if="!isSkillCompatible(element.id)"
                      >
                        Compétence non disponible en version Essentielle
                      </span>
                      {{ element.name }}
                    </div>
                  </template>
                </draggable>
              </div>
            </template>
          </draggable>
          <div v-else class="drag-and-drop-wrapper">
            <div class="ponderation-skill-container">
              <span class="title">Pondération neutre</span>
              <div class="skills-wrapper">
                <div
                  v-for="skill in modules.filter(
                    (m) => !m.disabled && m.id !== 'motivation'
                  )"
                  :key="skill.id_v2 ?? skill.id"
                  class="skill tooltip"
                  :class="{
                    disabled: !isSkillCompatible(skill.id_v2 ?? skill.id),
                  }"
                >
                  <span
                    class="tooltiptext"
                    v-if="!isSkillCompatible(skill.id_v2 ?? skill.id)"
                  >
                    Compétence non disponible en version Essentielle
                  </span>
                  {{ skill.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p class="italic muted">
          <i class="yuzu-icon yuzu-ai" style="display: inline-block" /> Yuzu AI
          s’appuie sur le référentiel de compétences de France Travail. Vos
          données ne sont ni conservées ni partagées avec des services tiers.
        </p>
        <div class="buttons-inline">
          <Button
            class="primary"
            :label="`Appliquer une pondération ${
              state.ponderation_type === 'neutral' ? 'manuelle' : 'neutre'
            }`"
            @click="
              state.ponderation_type =
                state.ponderation_type === 'neutral' ? 'coef' : 'neutral'
            "
          />
          <!-- <Button
            class="primary"
            label="Revenir à la pondération suggérée"
            @click="state.ponderation_type = 'coef'"
          /> -->
        </div>
      </div>
      <div class="buttons-inline">
        <Button
          class="secondary icon icon-left no-dark-mode campaign-button"
          label="Retourner à la création"
          icon="bi-chevron-left"
          @click="emits('prev')"
        />
        <Button
          class="icon primary no-dark-mode"
          label="Etape suivante"
          icon="bi-chevron-right"
          @click="
            emits('next', {
              name: state.campaign_name,
              workspace_id: state.workspace_id,
              skills_mandatory,
              skills_important,
              skills_secondary,
              calibration_id: currentCampaignVersionID,
            })
          "
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.subtitle {
  &.jobchoice {
    line-height: normal;

    .v-select {
      display: inline-block;
      min-width: 350px;
    }
  }

  &.adjust {
    color: #ffffffa6;
    font-size: 15px;
    line-height: normal;
  }
}

.duration {
  .group {
    :deep(.btn) {
      color: white;
    }
  }
}
</style>

<script setup lang="ts">
import { reactive, computed, onMounted, ref } from "vue";
import { useModulesStore } from "@/stores/modules.js";
import draggable from "vuedraggable";

import YuzuAiBanner from "./YuzuAiBanner.vue";
import { AISkillJob, AISkillsResult } from "./Step2.vue";

const emits = defineEmits(["prev", "next"]);

const { modules } = useModulesStore();

interface CampaignData {
  name: string;
  workspace_id: string;
  job: string;
  nojob: boolean;
  jobInfo: AISkillsResult;
  calibration_id: number;
}

const props = defineProps({
  campaignData: {
    type: Object as () => CampaignData,
  },
});

const state = reactive({
  campaign_name: props.campaignData.name,
  workspace_id: props.campaignData.workspace_id,
  ponderation_type: "coef",
  ponderation_obj: [
    {
      id: "mandatory",
      label: "compétences indispensables",
      skills: [],
    },
    {
      id: "important",
      label: "compétences importantes",
      skills: [],
    },
    {
      id: "secondary",
      label: "compétences secondaires",
      skills: [],
    },
    {
      id: "excluded",
      label: "compétences non prises en compte",
      skills: getAllSkills(),
    },
  ],
  campaignVersion: {
    major: 2,
    minor: 1,
    duration: 50,
  } as CampaignVersion,
});

const currentCampaignVersionID = computed(() => {
  return computedCampaignVersionID(state.campaignVersion);
});

interface VersionOption {
  label: string;
  version: CampaignVersion;
}

const versionOptions: VersionOption[] = [
  {
    label: "Version Essentielle - 25 min",
    version: {
      major: 2,
      minor: 1,
      duration: 25,
    },
  },
  {
    label: "Version Intégrale - 50 min",
    version: {
      major: 2,
      minor: 1,
      duration: 50,
    },
  },
];

function selectVersion(index: number) {
  state.campaignVersion = versionOptions[index].version;
}

interface Skill {
  id: string;
  name: string;
  disabled?: boolean;
}

function getAllSkills(): Skill[] {
  return modules
    .filter((skill) => !skill.disabled)
    .filter((skill) => skill.id !== "motivation") // Exclude motivation skill
    .map((skill) => {
      return {
        id: skill.id_v2 ?? skill.id,
        name: skill.name,
      };
    });
}

const useJob = computed(() => {
  return props.campaignData?.job && !props.campaignData.nojob;
});

const foundJobs = computed(() => {
  return (
    props.campaignData?.jobInfo?.results?.length &&
    props.campaignData?.jobInfo?.results?.length > 0
  );
});

const jobChoices = computed(() => {
  let jobs: any[] = [];
  jobs = jobs.concat(props.campaignData?.jobInfo.results);
  jobs.push({
    id: "nojob",
    job_title: "Aucun métier similaire",
  });
  return jobs;
});

const selectedJob = ref<any>(null);

function jobSelected(job: AISkillJob | { id: string }) {
  // If the job is "nojob", we reset the skills to what it was at start
  if ("id" in job && job.id === "nojob") {
    state.ponderation_obj.forEach((item) => {
      if (item.id === "excluded") return;
      item.skills = [];
    });
    state.ponderation_obj.find((item) => item.id === "excluded")!.skills =
      getAllSkills();
    return;
  }
  // Type guard so job becomes an AISkillJob
  if ("id" in job) return;

  const allSkills = getAllSkills();

  state.ponderation_obj.forEach((item) => {
    if (item.id === "excluded") return;
    if (!job.skills[item.id]) return;

    item.skills = [];

    job.skills[item.id as keyof typeof job.skills].forEach((aiSkill) => {
      // Find the index of the skill in the allSkills array
      const skillIndex = allSkills.findIndex((s) => s.id === aiSkill.name);
      // If the skill is found, remove it from allSkills and add it to the current item's skills
      if (skillIndex !== -1)
        item.skills.push(allSkills.splice(skillIndex, 1)[0]);
    });
  });

  // Add the remaining skills to the excluded category
  const excludedCategory = state.ponderation_obj.find(
    (type) => type.id === "excluded"
  );
  if (excludedCategory) excludedCategory.skills = allSkills;
}

const isSkillCompatible = (skillID) => {
  const compatiblesVersions = skillsCompatibleGameVersions[skillID];
  return (
    !compatiblesVersions ||
    compatiblesVersions.includes(currentCampaignVersionID.value)
  );
};

const skills_mandatory = computed(() => {
  const mandatory_obj = state.ponderation_obj.find(
    (type) => type.id === "mandatory"
  );

  const source =
    state.ponderation_type === "coef" ? mandatory_obj.skills : modules;

  return source.map((skill) => skill.id_v2 ?? skill.id).join(";");
});

const skills_important = computed(() => {
  const important_obj = state.ponderation_obj.find(
    (type) => type.id === "important"
  );

  return state.ponderation_type === "coef"
    ? important_obj.skills.map((skill) => skill.id_v2 ?? skill.id).join(";")
    : "neutral";
});

const skills_secondary = computed(() => {
  const secondary_obj = state.ponderation_obj.find(
    (type) => type.id === "secondary"
  );

  return state.ponderation_type === "coef"
    ? secondary_obj.skills.map((skill) => skill.id_v2 ?? skill.id).join(";")
    : "neutral";
});

onMounted(() => {
  // Auto select the first job if there is one
  if (useJob && foundJobs) {
    const firstJob = props.campaignData?.jobInfo?.results[0];
    if (firstJob) {
      selectedJob.value = firstJob;
      jobSelected(firstJob);
    }
  }
});
</script>
